import { useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import GameIcon from "components/admin/GameIcon";
import TemplatePicker from "components/admin/TemplatePicker";
import SelectField from "components/admin/SelectField";
import GiftEditor from "components/admin/GiftEditor";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import useConfirmChanges from "components/admin/confirmChanges";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatNumber } from "utils/format";

/** 1 Pet Coin = 100đ (docs/design/98-nap-tien.md mục 9), chỉ để admin ước giá tiền thật. */
const VND_PER_COIN = 100;

const giftText = (lines) =>
  (lines || []).map((l) => l.text || l.name || JSON.stringify(l)).join(", ") || "-";

const perkText = (perks, keys) =>
  keys
    .filter((k) => (perks?.[k.key] || 0) > 0)
    .map((k) => `${k.label}: ${perks[k.key]}`)
    .join(" · ") || "-";

function LevelDialog({ level, perkKeys, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState(() => ({
    need_coin: level.need_coin,
    name: level.name,
    gift: level.gift || [],
    perks: { ...(level.perks || {}) },
  }));
  const set = (patch) => setForm({ ...form, ...patch });
  const setPerk = (key, value) => set({ perks: { ...form.perks, [key]: Number(value) || 0 } });
  const submit = async () => {
    const yes = await ask({
      title: `Lưu VIP ${level.level}`,
      before: {
        need_coin: level.need_coin,
        name: level.name,
        gift: level.gift || [],
        perks: level.perks || {},
      },
      after: form,
      fields: [
        ["need_coin", "Ngưỡng (Pet Coin đã nạp)"],
        ["name", "Tên"],
        ["gift", "Quà lên cấp"],
        ["perks", "Quyền lợi"],
      ],
      notes: [
        Number(form.need_coin) < level.need_coin &&
          "Hạ ngưỡng: người đã đủ ngưỡng mới sẽ lên bậc ở lần nạp sau (hoặc khi bấm nhận quà còn thiếu).",
        "Quà lên cấp mới không gửi lại cho người đã nhận.",
      ],
    });
    if (!yes) return;
    const { ok } = await run(
      () =>
        api.put(`/api/admin/vip/levels/${level.level}`, {
          need_coin: Number(form.need_coin),
          name: form.name,
          gift: form.gift,
          perks: form.perks,
        }),
      (t) => t
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  return (
    <ActionDialog
      open
      title={`Sửa VIP ${level.level} (${level.groupName})`}
      description="Ngưỡng tính bằng tổng Pet Coin đã nạp (user.tongnap). Đổi quà lên cấp không gửi lại cho người đã nhận. Hạ ngưỡng thì người đủ bậc mới nhận quà ở lần nạp sau hoặc khi bấm nhận quà còn thiếu."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
      maxWidth="md"
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Ngưỡng (Pet Coin)"
            value={form.need_coin}
            helperText={`≈ ${formatNumber((Number(form.need_coin) || 0) * VND_PER_COIN)}đ`}
            onChange={(e) => set({ need_coin: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            label="Tên"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </Grid>
        {perkKeys.map((k) => (
          <Grid item xs={6} key={k.key}>
            <MDInput
              fullWidth
              type="number"
              label={k.label}
              value={form.perks[k.key] ?? 0}
              inputProps={{ min: 0 }}
              onChange={(e) => setPerk(k.key, e.target.value)}
            />
          </Grid>
        ))}
      </Grid>
      <GiftEditor
        value={form.gift}
        onChange={(gift) => set({ gift })}
        label="Quà lên cấp (thư không hết hạn, mọi món nên khoá giao dịch)"
        randomNote="Quà lên cấp nên cố định để người chơi biết trước mình nhận gì."
      />
    </ActionDialog>
  );
}

LevelDialog.propTypes = {
  level: PropTypes.shape({
    level: PropTypes.number,
    need_coin: PropTypes.number,
    name: PropTypes.string,
    groupName: PropTypes.string,
    gift: PropTypes.arrayOf(PropTypes.array),
    perks: PropTypes.object,
  }).isRequired,
  perkKeys: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function GroupDialog({ group, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [name, setName] = useState(group.name);
  const [gift, setGift] = useState(group.gift || []);
  const submit = async () => {
    const yes = await ask({
      title: `Lưu quà tuần nhóm ${group.name}`,
      before: { name: group.name, gift: group.gift || [] },
      after: { name, gift },
      fields: [
        ["name", "Tên nhóm"],
        ["gift", "Quà mỗi tuần"],
      ],
      notes: ["Áp dụng từ lần gửi quà tuần tiếp theo."],
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.put(`/api/admin/vip/groups/${group.grp}`, { name, gift }),
      (t) => t
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  return (
    <ActionDialog
      open
      title={`Quà tuần nhóm ${group.name}`}
      description="Tự gửi thư ở lần đăng nhập đầu mỗi tuần (từ thứ Hai), hoặc lúc qua tuần mới nếu đang online. Tuần không đăng nhập thì mất. Thư hạn 7 ngày."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
      maxWidth="md"
    >
      <MDInput fullWidth label="Tên nhóm" value={name} onChange={(e) => setName(e.target.value)} />
      <GiftEditor value={gift} onChange={setGift} label="Quà mỗi tuần" />
    </ActionDialog>
  );
}

GroupDialog.propTypes = {
  group: PropTypes.shape({
    grp: PropTypes.number,
    name: PropTypes.string,
    gift: PropTypes.arrayOf(PropTypes.array),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function PackDialog({ pack, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [price, setPrice] = useState(pack.price);
  const [items, setItems] = useState(() =>
    pack.items.map((x) => ({ itemId: x.itemId, count: x.count }))
  );
  const setItem = (i, patch) => setItems(items.map((x, j) => (i === j ? { ...x, ...patch } : x)));
  const submit = async () => {
    const yes = await ask({
      title: `Lưu gói VIP ${pack.level}`,
      before: {
        price: pack.price,
        items: pack.items.map((x) => ({ itemId: x.itemId, count: x.count })),
      },
      after: { price, items },
      fields: [
        [
          "price",
          "Giá",
          {
            text: (v) =>
              `${formatNumber(Number(v))} Pet Coin (≈ ${formatNumber(Number(v) * VND_PER_COIN)}đ)`,
          },
        ],
        ["items", "Các món trong gói"],
      ],
      notes: ["Gói người chơi đã mở không bị ảnh hưởng."],
    });
    if (!yes) return;
    const { ok } = await run(
      () =>
        api.put(`/api/admin/vip/packs/${pack.level}`, {
          price: Number(price),
          items: items.filter((x) => x.itemId).map((x) => [Number(x.itemId), Number(x.count)]),
        }),
      "Đã lưu gói VIP"
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  return (
    <ActionDialog
      open
      title={`Gói VIP ${pack.level}`}
      description="Mỗi tài khoản mua một lần, mở từ VIP cùng số. Mở trong túi ra đủ các món dưới đây, mọi món khoá giao dịch. Sửa nội dung không ảnh hưởng gói người chơi đã mở."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
      maxWidth="md"
    >
      <MDInput
        fullWidth
        type="number"
        label="Giá (Pet Coin)"
        value={price}
        inputProps={{ min: 1 }}
        helperText={`≈ ${formatNumber((Number(price) || 0) * VND_PER_COIN)}đ`}
        onChange={(e) => setPrice(e.target.value)}
      />
      {items.map((x, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Grid container spacing={1} key={i} alignItems="center" sx={{ mt: 1 }}>
          <Grid item xs={7}>
            <TemplatePicker
              kind="item"
              label={`Món ${i + 1}`}
              value={x.itemId}
              hideAdminItems
              onChange={(itemId) => setItem(i, { itemId })}
            />
          </Grid>
          <Grid item xs={3}>
            <MDInput
              fullWidth
              type="number"
              label="Số lượng"
              value={x.count}
              inputProps={{ min: 1 }}
              onChange={(e) => setItem(i, { count: e.target.value })}
            />
          </Grid>
          <Grid item xs={2}>
            <MDButton
              size="small"
              variant="text"
              color="error"
              onClick={() => setItems(items.filter((_, j) => j !== i))}
            >
              <Icon>delete</Icon>
            </MDButton>
          </Grid>
        </Grid>
      ))}
      <MDButton
        size="small"
        variant="text"
        color="info"
        sx={{ mt: 1 }}
        onClick={() => setItems([...items, { itemId: null, count: 1 }])}
      >
        <Icon>add</Icon>&nbsp;Thêm món
      </MDButton>
    </ActionDialog>
  );
}

PackDialog.propTypes = {
  pack: PropTypes.shape({
    level: PropTypes.number,
    price: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

const EMPTY_SHOP = {
  itemId: null,
  count: 1,
  basePrice: 1000,
  rarity: 0,
  limitCount: 5,
  minVip: 1,
  sort: 0,
  enabled: true,
};

function ShopDialog({ row, rarities, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState(() => (row ? { ...row } : { ...EMPTY_SHOP }));
  const set = (patch) => setForm({ ...form, ...patch });
  const submit = async () => {
    const yes = await ask({
      title: row ? `Lưu ${row.name}` : "Thêm hàng Shop ưu đãi VIP",
      isNew: !row,
      before: row,
      after: form,
      fields: [
        ["itemId", "Vật phẩm (mã)"],
        ["count", "Số lượng mỗi lượt"],
        ["basePrice", "Giá gốc (vàng)"],
        [
          "rarity",
          "Độ quý hiếm",
          { text: (v) => rarities.find((x) => x.value === Number(v))?.label || v },
        ],
        ["limitCount", "Lượt mỗi kỳ"],
        ["minVip", "Mở từ VIP"],
        ["sort", "Thứ tự"],
        ["enabled", "Đang bán"],
      ],
      notes: ["Áp dụng ngay cho mọi người chơi."],
    });
    if (!yes) return;
    const body = {
      itemId: Number(form.itemId) || 0,
      count: Number(form.count),
      basePrice: Number(form.basePrice),
      rarity: Number(form.rarity),
      limitCount: Number(form.limitCount),
      minVip: Number(form.minVip),
      sort: Number(form.sort),
      enabled: Boolean(form.enabled),
    };
    const { ok } = await run(
      () =>
        row
          ? api.put(`/api/admin/vip/shop/${row.id}`, body)
          : api.post("/api/admin/vip/shop", body),
      "Đã lưu dòng hàng"
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  return (
    <ActionDialog
      open
      title={row ? `Sửa ${row.name}` : "Thêm hàng Shop ưu đãi VIP"}
      description="Trả bằng vàng. Giá gốc nên bằng giá cùng món ở shop ngoài (Thương nhân). Giá bán = giá gốc trừ % giảm theo bậc VIP. Thường đổi lượt mỗi ngày, Hiếm mỗi tuần, Quý mỗi tháng, Cực quý mỗi quý. Từ Bạch kim lượt hàng Thường, Hiếm nhân hệ số."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TemplatePicker
            kind="item"
            label="Vật phẩm"
            value={form.itemId}
            hideAdminItems
            onChange={(itemId) => set({ itemId })}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Số lượng mỗi lượt"
            value={form.count}
            inputProps={{ min: 1 }}
            onChange={(e) => set({ count: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Giá gốc cả lượt (vàng)"
            value={form.basePrice}
            inputProps={{ min: 1 }}
            onChange={(e) => set({ basePrice: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <SelectField
            fullWidth
            label="Độ quý hiếm"
            value={form.rarity}
            onChange={(e) => set({ rarity: Number(e.target.value) })}
          >
            {rarities.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </SelectField>
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Lượt mỗi kỳ"
            value={form.limitCount}
            inputProps={{ min: 1 }}
            onChange={(e) => set({ limitCount: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Mở từ VIP"
            value={form.minVip}
            inputProps={{ min: 1, max: 15 }}
            onChange={(e) => set({ minVip: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Thứ tự"
            value={form.sort}
            onChange={(e) => set({ sort: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.enabled)}
                onChange={(e) => set({ enabled: e.target.checked })}
              />
            }
            label="Đang bán"
          />
        </Grid>
      </Grid>
    </ActionDialog>
  );
}

ShopDialog.defaultProps = { row: null };

ShopDialog.propTypes = {
  row: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  rarities: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function VipTab() {
  const canEdit = useCan(PERMISSIONS.VIP_EDIT);
  const data = useFetch(() => api.get("/api/admin/vip"), []);
  const [level, setLevel] = useState(null);
  const [group, setGroup] = useState(null);
  const [pack, setPack] = useState(null);
  const [shopRow, setShopRow] = useState(null);
  const [addingShop, setAddingShop] = useState(false);
  const [run] = useRunner();
  const confirm = useConfirm();
  const reload = () => data.reload(true);
  const d = data.data;
  const perkKeys = d?.perkKeys || [];

  const removeShop = async (row) => {
    const ok = await confirm({
      title: `Xoá ${row.name}?`,
      message:
        "Người chơi không còn thấy món này trong Shop ưu đãi VIP. Lượt đã mua vẫn giữ trong DB.",
      confirmText: "Xoá",
      color: "error",
    });
    if (!ok) return;
    const { ok: done } = await run(
      () => api.del(`/api/admin/vip/shop/${row.id}`),
      (t) => t
    );
    if (done) reload();
  };

  const editButton = (onClick) =>
    canEdit && (
      <MDButton size="small" variant="text" color="warning" onClick={onClick}>
        <Icon>edit</Icon>&nbsp;Sửa
      </MDButton>
    );

  return (
    <Grid container spacing={3}>
      {data.error && (
        <Grid item xs={12}>
          <Alert severity="error">{data.error}</Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <PageCard
          title="15 bậc VIP"
          subtitle={
            d
              ? `${formatNumber(d.stats.accounts)} tài khoản có VIP · ${formatNumber(
                  d.stats.claims
                )} thư quà lên cấp đã gửi · ${formatNumber(
                  d.stats.weeklyThisWeek
                )} quà tuần đã gửi tuần này. Điểm VIP = tổng Pet Coin đã nạp, không giảm.`
              : "Điểm VIP = tổng Pet Coin đã nạp"
          }
          color="info"
          noPadding
        >
          <AdminTable
            loading={data.loading}
            rows={d?.levels || []}
            rowKey="level"
            columns={[
              { key: "level", label: "VIP", width: 50 },
              { key: "groupName", label: "Nhóm" },
              {
                key: "need_coin",
                label: "Ngưỡng",
                align: "right",
                render: (r) => `${formatNumber(r.need_coin)} PC (≈ ${formatNumber(r.needVnd)}đ)`,
              },
              {
                key: "accounts",
                label: "Tài khoản",
                align: "right",
                render: (r) => formatNumber(r.accounts),
              },
              {
                key: "giftLines",
                label: "Quà lên cấp",
                render: (r) => (
                  <MDTypography variant="caption">
                    {r.titleName && (
                      <Chip size="small" color="warning" label={r.titleName} sx={{ mr: 0.5 }} />
                    )}
                    {giftText(r.giftLines)}
                  </MDTypography>
                ),
              },
              {
                key: "perks",
                label: "Quyền lợi",
                render: (r) => (
                  <MDTypography variant="caption">{perkText(r.perks, perkKeys)}</MDTypography>
                ),
              },
              canEdit && {
                key: "actions",
                label: "",
                align: "right",
                render: (r) => editButton(() => setLevel(r)),
              },
            ].filter(Boolean)}
          />
        </PageCard>
      </Grid>
      <Grid item xs={12}>
        <PageCard title="Quà tuần theo nhóm" color="warning" noPadding>
          <AdminTable
            loading={data.loading}
            rows={d?.groups || []}
            rowKey="grp"
            columns={[
              {
                key: "name",
                label: "Nhóm",
                render: (r) => (
                  <MDBox display="flex" gap={1} alignItems="center">
                    <GameIcon path={r.icon} size={24} fallback="workspace_premium" />
                    {r.name}
                  </MDBox>
                ),
              },
              { key: "giftLines", label: "Quà mỗi tuần", render: (r) => giftText(r.giftLines) },
              canEdit && {
                key: "actions",
                label: "",
                align: "right",
                render: (r) => editButton(() => setGroup(r)),
              },
            ].filter(Boolean)}
          />
        </PageCard>
      </Grid>
      <Grid item xs={12}>
        <PageCard
          title="Gói VIP (Shop Pet Coin)"
          subtitle="Mỗi bậc một gói, mỗi tài khoản mua một lần, mở từ VIP cùng số"
          color="success"
          noPadding
        >
          <AdminTable
            loading={data.loading}
            rows={d?.packs || []}
            rowKey="level"
            columns={[
              {
                key: "level",
                label: "Gói",
                render: (r) => (
                  <MDBox display="flex" gap={1} alignItems="center">
                    <GameIcon path={`items/${r.itemId}.png`} size={28} fallback="redeem" />
                    VIP {r.level}
                  </MDBox>
                ),
              },
              {
                key: "price",
                label: "Giá",
                align: "right",
                render: (r) =>
                  `${formatNumber(r.price)} PC (≈ ${formatNumber(r.price * VND_PER_COIN)}đ)`,
              },
              {
                key: "items",
                label: "Nội dung",
                render: (r) => (
                  <MDTypography variant="caption">
                    {r.items.map((x) => `${x.name}${x.count > 1 ? ` x${x.count}` : ""}`).join(", ")}
                  </MDTypography>
                ),
              },
              {
                key: "buyers",
                label: "Đã mua",
                align: "right",
                render: (r) => formatNumber(r.buyers),
              },
              canEdit && {
                key: "actions",
                label: "",
                align: "right",
                render: (r) => editButton(() => setPack(r)),
              },
            ].filter(Boolean)}
          />
        </PageCard>
      </Grid>
      <Grid item xs={12}>
        <PageCard
          title="Shop ưu đãi VIP"
          subtitle="Trả bằng vàng, giá giảm theo bậc VIP. Lượt: Thường mỗi ngày, Hiếm mỗi tuần, Quý mỗi tháng, Cực quý mỗi quý"
          color="info"
          noPadding
          actions={
            canEdit && (
              <MDButton size="small" color="white" onClick={() => setAddingShop(true)}>
                <Icon>add</Icon>&nbsp;Thêm hàng
              </MDButton>
            )
          }
        >
          <AdminTable
            loading={data.loading}
            rows={d?.shop || []}
            rowKey="id"
            emptyText="Shop trống"
            columns={[
              {
                key: "name",
                label: "Hàng",
                render: (r) => (
                  <MDBox display="flex" gap={1} alignItems="center">
                    <GameIcon path={r.icon} size={28} fallback="inventory_2" />
                    {r.name}
                    {r.count > 1 && ` x${r.count}`}
                    {!r.enabled && <Chip size="small" label="Tắt" />}
                  </MDBox>
                ),
              },
              { key: "rarityName", label: "Hạng" },
              { key: "minVip", label: "Mở từ", render: (r) => `VIP ${r.minVip}` },
              { key: "limitCount", label: "Lượt/kỳ", align: "right" },
              {
                key: "basePrice",
                label: "Giá gốc",
                align: "right",
                render: (r) => (
                  <>
                    {formatNumber(r.basePrice)}
                    {r.outsidePrice != null && r.outsidePrice !== r.basePrice && (
                      <Chip
                        size="small"
                        color="error"
                        sx={{ ml: 0.5 }}
                        label={`Thương nhân ${formatNumber(r.outsidePrice)}`}
                      />
                    )}
                  </>
                ),
              },
              {
                key: "prices",
                label: "VIP 1 / 10 / 15",
                align: "right",
                render: (r) =>
                  `${formatNumber(r.priceVip1)} / ${formatNumber(r.priceVip10)} / ${formatNumber(
                    r.priceVip15
                  )}`,
              },
              canEdit && {
                key: "actions",
                label: "",
                align: "right",
                render: (r) => (
                  <>
                    {editButton(() => setShopRow(r))}
                    <MDButton
                      size="small"
                      variant="text"
                      color="error"
                      onClick={() => removeShop(r)}
                    >
                      <Icon>delete</Icon>
                    </MDButton>
                  </>
                ),
              },
            ].filter(Boolean)}
          />
        </PageCard>
      </Grid>
      {level && (
        <LevelDialog
          level={level}
          perkKeys={perkKeys}
          onClose={() => setLevel(null)}
          onDone={reload}
        />
      )}
      {group && <GroupDialog group={group} onClose={() => setGroup(null)} onDone={reload} />}
      {pack && <PackDialog pack={pack} onClose={() => setPack(null)} onDone={reload} />}
      {(addingShop || shopRow) && (
        <ShopDialog
          row={shopRow}
          rarities={d?.rarities || []}
          onClose={() => {
            setAddingShop(false);
            setShopRow(null);
          }}
          onDone={reload}
        />
      )}
    </Grid>
  );
}

export default VipTab;
